import { Box, Fab, IconButton, Theme, Tooltip, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { sortByKey } from "@notemeal/utils-sort";
import { MouseEvent, useEffect, useState } from "react";
import { animated } from "react-spring";
import { useRecipeWithFullIngredientsQuery } from "apps/web/src/types";
import { DeconstructIcon } from "../global/Icons";
import { Loading } from "../global/Loading";
import { DeconstructionDialog } from "./DeconstructionDialog";
import { formatAmbiguousServingAmountWithTotalWeight } from "./utils";

export const LOADING_INGREDIENTS_HEIGHT = 96;

/** Per-line height for redesign ingredient rows (fontSize 12 / lineHeight 20). */
export const REDESIGN_INGREDIENT_LINE_HEIGHT = 20;
/** Yield row with optional deconstruct control. */
export const REDESIGN_YIELD_ROW_HEIGHT = 28;
/** Vertical padding on redesign ingredients panel (pt 4 + pb 8). */
export const REDESIGN_INGREDIENTS_PANEL_PADDING_Y = 12;

export const getRedesignExpandedIngredientsHeight = (ingredientsCount: number): number =>
  ingredientsCount * REDESIGN_INGREDIENT_LINE_HEIGHT + REDESIGN_YIELD_ROW_HEIGHT + REDESIGN_INGREDIENTS_PANEL_PADDING_Y;

const useStyles = makeStyles(({ spacing, palette: { primary, secondary } }: Theme) =>
  createStyles({
    root: {
      borderBottomLeftRadius: spacing(),
      borderBottomRightRadius: spacing(),
      cursor: "pointer",
      backgroundColor: "#eaeaea",
    },
    ingredients: {
      display: "flex",
      flexDirection: "column",
      alignItems: "stretch",
      paddingLeft: spacing(9),
      borderBottomLeftRadius: spacing(),
      borderBottomRightRadius: spacing(),
    },
    ingredient: {
      margin: spacing(0, 1),
      lineHeight: 2,
    },
    yield: {
      margin: spacing(0, 1),
      lineHeight: 2,
      fontWeight: "bold",
    },
    expandIngredients: {
      display: "flex",
      justifyContent: "center",
      backgroundColor: primary.light,
      borderBottomLeftRadius: spacing(),
      borderBottomRightRadius: spacing(),
      fontSize: 16,
      height: spacing(2),
    },
    collapseIngredients: {
      display: "flex",
      justifyContent: "center",
      backgroundColor: secondary.light,
      borderBottomLeftRadius: spacing(),
      borderBottomRightRadius: spacing(),
      fontSize: 16,
      height: spacing(2),
    },
    deconstructButton: {
      position: "absolute",
      bottom: spacing(),
      right: spacing(),
    },
    loading: {
      height: LOADING_INGREDIENTS_HEIGHT,
    },
  })
);

interface ServingAmountsIngredientsListProps {
  recipeId: string;
  perRecipeYield: number;
  units: string;
  onClose: () => void;
  onDeconstruct?: () => void;
  /** Show confirmation when deconstruct may change nutrients (manual recipe macros or serving-own nutrients). */
  shouldConfirmDeconstruct?: boolean;
  onLoad?: (ingredientsCount: number) => void;
  style?: any;
  variant?: "legacy" | "redesign";
}

export const ServingAmountsIngredientsList = ({
  recipeId,
  style,
  onClose,
  onDeconstruct,
  perRecipeYield,
  units,
  onLoad,
  shouldConfirmDeconstruct = false,
  variant = "legacy",
}: ServingAmountsIngredientsListProps) => {
  const classes = useStyles();

  const { data, loading } = useRecipeWithFullIngredientsQuery({
    variables: {
      id: recipeId,
    },
  });
  const ingredients = data?.recipe.ingredients ?? [];

  useEffect(() => {
    if (data?.recipe.ingredients && onLoad) {
      onLoad(data.recipe.ingredients.length);
    }
    // Intentionally omit onLoad: parent passes an inline callback each render.
  }, [data?.recipe.ingredients?.length]);

  const [deconstructing, setDeconstructing] = useState(false);
  const [showRecipeDeconstructionWarning, setShowRecipeDeconstructionWarning] = useState<boolean>(false);

  const handleDeconstructRecipe = () => {
    if (onDeconstruct) {
      setDeconstructing(true);
      onDeconstruct();
    }
  };

  const onDeconstructClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (!shouldConfirmDeconstruct) {
      handleDeconstructRecipe();
    } else {
      setShowRecipeDeconstructionWarning(true);
    }
  };

  const deconstructionDialog = showRecipeDeconstructionWarning && (
    <DeconstructionDialog
      open={showRecipeDeconstructionWarning}
      onClose={() => setShowRecipeDeconstructionWarning(false)}
      onConfirm={handleDeconstructRecipe}
    />
  );

  if (variant === "redesign") {
    if (loading && ingredients.length === 0) {
      return (
        <Box
          onClick={onClose}
          sx={{
            height: LOADING_INGREDIENTS_HEIGHT,
            bgcolor: "greyscale.100",
            borderRadius: "0 0 6px 6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          <Loading progressSize="xs" />
        </Box>
      );
    }

    return (
      <>
        <Box
          onClick={onClose}
          sx={{
            bgcolor: "greyscale.100",
            borderRadius: "0 0 6px 6px",
            pt: 0.5,
            pb: 1,
            pl: 5,
            pr: 1,
            cursor: "pointer",
          }}
        >
          {sortByKey(ingredients, "position").map(({ id, serving, amount }) => (
            <Typography
              key={id}
              noWrap
              sx={{
                fontSize: 12,
                lineHeight: `${REDESIGN_INGREDIENT_LINE_HEIGHT}px`,
                color: "text.secondary",
                m: 0,
              }}
            >
              {serving.foodOrRecipe.name} (
              {formatAmbiguousServingAmountWithTotalWeight({
                amount,
                serving,
              })}
              )
            </Typography>
          ))}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              minHeight: REDESIGN_YIELD_ROW_HEIGHT,
            }}
          >
            <Typography
              noWrap
              sx={{
                fontSize: 12,
                lineHeight: `${REDESIGN_INGREDIENT_LINE_HEIGHT}px`,
                fontWeight: 600,
                color: "text.primary",
                m: 0,
                minWidth: 0,
              }}
            >
              Yields {perRecipeYield} {units}
            </Typography>
            {onDeconstruct && (
              <Tooltip title="Deconstruct recipe">
                <IconButton
                  disabled={deconstructing}
                  onClick={onDeconstructClick}
                  aria-label="Deconstruct recipe"
                  size="small"
                  sx={{
                    width: 28,
                    height: 28,
                    flexShrink: 0,
                    border: 1,
                    borderColor: "greyscale.300",
                    borderRadius: "6px",
                    p: 0,
                  }}
                >
                  {deconstructing ? <Loading progressSize="xs" /> : <DeconstructIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
        {deconstructionDialog}
      </>
    );
  }

  const ingredientComponents = ingredients ? (
    <div className={classes.ingredients}>
      {sortByKey(ingredients, "position").map(({ id, serving, amount }) => {
        const totalAmount = amount;
        return (
          <Typography key={id} variant="subtitle1" className={classes.ingredient} noWrap>
            {serving.foodOrRecipe.name} (
            {formatAmbiguousServingAmountWithTotalWeight({
              amount: totalAmount,
              serving,
            })}
            )
          </Typography>
        );
      })}
      <Typography variant="subtitle1" className={classes.yield} noWrap>
        Yields {perRecipeYield} {units}
      </Typography>
      {onDeconstruct && (
        <Tooltip title={"Deconstruct recipe"}>
          <Fab
            disabled={deconstructing}
            size="small"
            color={"primary"}
            onClick={onDeconstructClick}
            className={classes.deconstructButton}
          >
            {deconstructing ? <Loading progressSize="xs" /> : <DeconstructIcon />}
          </Fab>
        </Tooltip>
      )}
    </div>
  ) : (
    <div className={classes.loading}>
      <Loading progressSize="xs" />
    </div>
  );

  return (
    <>
      <animated.div className={classes.root} style={style} onClick={onClose}>
        {ingredientComponents}
      </animated.div>
      {deconstructionDialog}
    </>
  );
};
